import Styles from './DesignDownloadDrawer.module.scss'
import { ExportedFrame } from 'components/VisualStudioPlayground/VisualEditor/utils/export/exportDesign'
import { observer } from 'mobx-react-lite'
import { DesignPreview } from 'api/clients/DesignClient'
import { FileCard } from './FileCard'
import { StalePreviewOverlay } from 'components/PostPreviewModal/components/StalePreviewOverlay'
import { useMemo, useRef, useState } from 'react'
import cx from 'classnames'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { reportError } from 'utils/error'

interface Props {
  width?: number
  height?: number
  previews: DesignPreview[]
  exportFiles: ExportedFrame[]
  onPress: (fileToDownload: ExportedFrame) => void
  downloadsCount: number
}

export const DesignDownloadBody = observer(({ previews, exportFiles, onPress, downloadsCount }: Props) => {
  const imageRef = useRef<HTMLImageElement>(null)
  const { isMobile } = useResponsiveDesign()
  const [showImagePlaceholder, setShowImagePlaceholder] = useState(true)
  const retryCountRef = useRef(0)
  const videoRef = useRef<HTMLVideoElement>(null)

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    reportError(e)
    // Retry fetching the video 3 times if it's empty
    if (retryCountRef.current < 3 && videoRef.current) {
      retryCountRef.current += 1

      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.load()
        }
      }, 1000 * retryCountRef.current)
    } else {
      setShowImagePlaceholder(false)
    }
  }

  const width = imageRef.current?.naturalWidth
  const height = imageRef.current?.naturalHeight
  const aspectRatio = useMemo(() => {
    if (!width || !height) return undefined

    return width > height ? undefined : width / height
  }, [height, width])

  if (!previews.length || (previews.length > 1 && !exportFiles.length))
    return (
      <div className={Styles.loadingState}>
        <StalePreviewOverlay
          label={downloadsCount > 1 ? 'Preparing your downloads' : 'Preparing your download'}
          style={isMobile ? undefined : { backgroundColor: 'transparent' }}
        />
      </div>
    )

  if (previews.length === 1)
    return (
      <div key={previews[0].url} className={Styles.preview}>
        {previews[0].mime_type.includes('image') ? (
          <img src={previews[0].url} alt="preview" ref={imageRef} style={{ aspectRatio }} className={Styles.image} />
        ) : (
          <>
            {showImagePlaceholder && (
              <LoadingShimmerer borderRadius={5} className={Styles.loadingShimmerer}>
                <img src={previews[0].still} alt="preview" style={{ aspectRatio }} className={Styles.image} />
              </LoadingShimmerer>
            )}
            <video
              ref={videoRef}
              controls
              src={previews[0].url}
              className={cx(Styles.videoPreview, { [Styles.hidden]: showImagePlaceholder })}
              autoPlay
              data-testid="video-preview"
              onLoadedData={() => {
                setShowImagePlaceholder(false)
              }}
              onError={handleVideoError}
            />
          </>
        )}
      </div>
    )

  return (
    <div className={Styles.multiplePreviews}>
      {exportFiles.map((exportFile, index) => (
        <FileCard
          key={exportFile.frameId}
          preview={previews[index]}
          designFile={exportFile}
          onPress={() => onPress(exportFile)}
        />
      ))}
    </div>
  )
})
